import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

function PaymentThankYou() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const certificateId = searchParams.get("certificate_id");
  const urlName = searchParams.get("name");
  const urlDomain = searchParams.get("domain");
  
  const [name, setName] = React.useState(urlName || "there");
  const [domain, setDomain] = React.useState(urlDomain || "Internship Program");

  React.useEffect(() => {
    // Set from URL params immediately
    if (urlName) setName(urlName);
    if (urlDomain) setDomain(urlDomain);

    if (!certificateId) return;

    fetch(`${process.env.REACT_APP_API_URL}/api/payment/payment-result?certificate_id=${certificateId}`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.domain) {
          setDomain(data.domain);
        }
        if (data.success && data.name) {
          setName(data.name);
        }
      })
      .catch(err => console.error("Failed to fetch payment result:", err));
  }, [certificateId, urlName, urlDomain]);

  // ✅ SAFE LinkedIn share (text-only, verify link)
  const linkedInShareUrl = React.useMemo(() => {
    if (!certificateId) return "#";

    const captionLines = [
      `Excited to complete my ${domain} internship with Syntriq 🚀`,
      ``,
      `Hands-on learning, real-world projects, and practical experience.`,
      ``,
      `🔍 Verify my certificate here:`,
      `https://syntriq.in/verify/${certificateId}`,
      ``,
      `#SyntriqInternship #Internship #CareerGrowth @Syntriq`
    ];

    const caption = captionLines.join("\n");

    return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
      caption
    )}`;
  }, [certificateId, domain]);

  const sections = [
    {
      icon: '📩', title: 'Certificate Sent to Your Email',
      content: (
        <p style={{ color: "#64748b", marginTop: '0.5rem', lineHeight: 1.7 }}>
          Your certificate has been sent to your{" "}
          <strong>registered email address</strong>. Please check spam before contacting support.
          <br />You can download it anytime from our website.
        </p>
      ),
    },
    {
      icon: '🔗', title: 'Share Your Achievement',
      content: (
        <>
          <p style={{ color: "#64748b", marginTop: '0.5rem' }}>
            Share your verified certificate with recruiters and your network.
          </p>
          {certificateId && (
            <a href={linkedInShareUrl} target="_blank" rel="noopener noreferrer" className="btn btn-linkedin mt-2">
              🔗 Share on LinkedIn
            </a>
          )}
          <p style={{ fontSize: "0.85rem", marginTop: "0.5rem", color: "#64748b" }}>
            Caption is pre-filled — just click <strong>Post</strong>.
          </p>
        </>
      ),
    },
    {
      icon: '📥', title: 'Download Your Certificate',
      content: (
        <>
          <p style={{ color: "#64748b", marginTop: '0.5rem' }}>
            Enter your registered email to download your certificate anytime.
          </p>
          <button className="btn btn-primary mt-2" onClick={() => navigate("/download")}>
            📥 Download Certificate
          </button>
        </>
      ),
    },
    {
      icon: '🔍', title: 'Verify Your Certificate',
      content: (
        <>
          <p style={{ color: "#64748b", marginTop: '0.5rem' }}>
            Verify authenticity using the Certificate ID or QR code.
          </p>
          <button className="btn btn-secondary mt-2" onClick={() => navigate("/verify")}>
            🔍 Verify Certificate
          </button>
        </>
      ),
    },
    {
      icon: '📱', title: 'About the QR Code',
      content: (
        <p style={{ color: "#64748b", marginTop: '0.5rem' }}>
          The QR code opens the official verification page — tamper-proof and recruiter-friendly.
        </p>
      ),
    },
  ];

  return (
    <Layout>
      <div className="page-container">
        <div className="card reveal reveal-up shimmer-border" style={{ textAlign: "center", position: 'relative' }}>
          <div style={{ fontSize: '4rem', marginBottom: '0.5rem', animation: 'floatSoft 2.5s ease-in-out infinite' }}>🎉</div>
          <h1 className="page-title">
            Thank You, <span className="gradient-text">{name || "there"}</span>!
          </h1>

          <p className="page-subtitle" style={{ marginTop: "0.5rem" }}>
            Thank you for supporting our internship program.
          </p>

          <p style={{ marginTop: "1rem", color: "#64748b" }}>
            Your contribution of{" "}
            <strong style={{
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>₹99</strong>{" "}
            helps us appreciate the efforts of our mentors, reviewers, and support team.
          </p>

          {sections.map((section, i) => (
            <React.Fragment key={section.title}>
              <hr />
              <div className="reveal reveal-up" style={{ transitionDelay: `${i * 100}ms` }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <span className="float-soft" style={{ display: 'inline-block' }}>{section.icon}</span>
                  {section.title}
                </h3>
                {section.content}
              </div>
            </React.Fragment>
          ))}

          <hr />

          <a href="mailto:support@syntriq.in" style={{ textDecoration: 'none' }}>
            <p style={{ fontSize: "0.9rem", color: "#64748b", transition: 'color 0.3s' }}>
              Need help? Contact <strong style={{ color: '#2563eb' }}>support@syntriq.in</strong>
            </p>
          </a>
        </div>
      </div>
    </Layout>
  );
}

export default PaymentThankYou;